import React, { useMemo } from 'react';
import {
  Bar, BarChart, CartesianGrid, Cell, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import {
  Ban, Bot, CheckCircle2, CircleDashed, IndianRupee, Layers, MousePointerClick, PackageCheck, ShoppingBag, UserRound,
} from 'lucide-react';
import StatCard from '../components/StatCard';
import { EMPTY, PANEL, SECTION_TITLE } from '../components/tableStyles';
import { useReport } from '../ReportContext';
import { inr, lakh, num, pct } from '../format';
import { totals } from '../stats';
import { BUCKETS, CHART, WORK_BUCKETS } from '../theme';
import type { OpenDrill, WorkRow } from '../types';

interface Props { rows: WorkRow[]; openDrill: OpenDrill }

const OverviewTab: React.FC<Props> = ({ rows, openDrill }) => {
  const { automation: AUTOMATION, trend, uniqueCustomers } = useReport();
  const t = useMemo(() => totals(rows), [rows]);
  const auto = AUTOMATION.engatiAi + AUTOMATION.abcAutoUpdated + AUTOMATION.otherSystem;

  const perBucket = WORK_BUCKETS.map(b => {
    const bt = totals(rows.filter(r => r.bucket === b));
    return { key: b, name: BUCKETS[b].label, received: bt.received, worked: bt.worked };
  }).filter(d => d.received > 0);

  const revenueByBucket = [...WORK_BUCKETS, 'none' as const].map(b => ({ key: b, name: BUCKETS[b].label, revenue: totals(rows.filter(r => r.bucket === b)).validRevenue }))
    .filter(d => d.revenue > 0);

  if (rows.length === 0) return <div className={EMPTY}>No work or orders for this selection.</div>;

  const work = (title: string, total: number, worked?: 'worked' | 'unworked', bucket: 'all' | 'abc' = 'all') => () =>
    openDrill({ title, kind: 'work', agent: 'all', bucket, total, worked });
  const orders = (title: string, total: number, orderStatus?: 'valid' | 'cancelled') => () =>
    openDrill({ title, kind: 'orders', agent: 'all', bucket: 'all', total, orderStatus });

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard size="lg" label="Work items" value={num(t.workItems)} tone="lavender" icon={<Layers className="h-5 w-5" />}
          info="Every bucket in one unit: ABC cart events, Reorder rows, EDD/NDR/RTO issues, agent leads and phone leads."
          sub={`${num(t.uniqueCustomers)} unique customers across buckets`} onClick={work('Work items', t.workItems)} />
        <StatCard size="lg" label="Worked" value={`${num(t.worked)} / ${num(t.received)}`} tone="mint" icon={<CheckCircle2 className="h-5 w-5" />}
          info="Worked = an agent updated a disposition. Assigned, viewed, call-only, note-only and automation do not count. For ABC, one disposition covers every cart that arrived before it."
          sub={`${pct(t.workRate)} worked · ${num(t.unworked)} unworked`}
          onClick={work('Work items worked', t.worked, 'worked')} />
        <StatCard size="lg" label="Valid orders" value={num(t.validOrders)} tone="sky" icon={<ShoppingBag className="h-5 w-5" />}
          sub={`${num(t.orders)} orders placed`} onClick={orders('Valid orders', t.validOrders, 'valid')} />
        <StatCard size="lg" label="Valid revenue" value={lakh(t.validRevenue)} tone="peach" icon={<IndianRupee className="h-5 w-5" />}
          info="Valid orders only (not cancelled, voided or refunded). Each order counted once."
          sub={t.aov ? `AOV ${inr(t.aov)} · ${num(t.units)} units` : undefined} onClick={orders('Valid orders', t.validOrders, 'valid')} />
      </div>

      <section>
        <h2 className={`${SECTION_TITLE} mb-3 text-lg`}>Performance at a glance</h2>
        <div className="grid gap-4 lg:grid-cols-2">
          <div className={`${PANEL} p-5`}>
            <h3 className={SECTION_TITLE}>Received vs worked, by bucket</h3>
            <p className="mb-3 text-xs text-slate-600">Every bucket in work items.</p>
            <div className="h-60">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={perBucket} margin={{ left: -10 }}>
                  <CartesianGrid stroke={CHART.grid} strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} /><YAxis tick={{ fontSize: 12 }} />
                  <Tooltip /><Legend />
                  <Bar dataKey="received" name="Received" fill={CHART.received} radius={[6, 6, 0, 0]} />
                  <Bar dataKey="worked" name="Worked" fill={CHART.worked} radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className={`${PANEL} p-5`}>
            <h3 className={SECTION_TITLE}>Valid revenue by bucket</h3>
            <p className="mb-3 text-xs text-slate-600">Each order sits in one bucket: the tag set on the order, otherwise the customer's open work at the time of the order.</p>
            <div className="h-60">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={revenueByBucket}>
                  <CartesianGrid stroke={CHART.grid} strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} /><YAxis tick={{ fontSize: 12 }} tickFormatter={(v: number) => lakh(v)} />
                  <Tooltip formatter={(v: number) => inr(v)} />
                  <Bar dataKey="revenue" name="Valid revenue" radius={[6, 6, 0, 0]}>
                    {revenueByBucket.map(d => <Cell key={d.key} fill={BUCKETS[d.key].hex} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
        <div className={`${PANEL} mt-4 p-5`}>
          <h3 className={`${SECTION_TITLE} mb-3`}>Orders per day — valid vs cancelled (last 14 days)</h3>
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={trend} margin={{ left: -10 }}>
                <CartesianGrid stroke={CHART.grid} strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="day" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 12 }} />
                <Tooltip /><Legend />
                <Bar dataKey="valid" name="Valid" stackId="o" fill={CHART.worked} />
                <Bar dataKey="cancelled" name="Cancelled" stackId="o" fill={CHART.cancelled} radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>

      <section>
        <h2 className={`${SECTION_TITLE} mb-3 text-lg`}>More insights</h2>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
          <StatCard size="sm" label="Unique customers" value={num(uniqueCustomers)} tone="sky" icon={<UserRound className="h-4 w-4" />}
            info="Each customer (phone) counted once, even when they appear in several buckets or have several work items." />
          <StatCard size="sm" label="Unique orders (order work)" value={num(t.uniqueOrders)} tone="butter" icon={<PackageCheck className="h-4 w-4" />}
            info="EDD, NDR and RTO work is order-level: distinct order IDs, counted once however many times the order came up." />
          <StatCard size="sm" label="Automation handled" value={num(auto)} tone="slate" icon={<Bot className="h-4 w-4" />}
            info="Engati AI calls and system closes. Shown for visibility; never counted as worked."
            sub={`Engati ${num(AUTOMATION.engatiAi)} · Auto Updated ${num(AUTOMATION.abcAutoUpdated)}`} />
          <StatCard size="sm" label="Orders placed" value={num(t.orders)} tone="sky" icon={<ShoppingBag className="h-4 w-4" />}
            info="Every order placed, valid and cancelled." sub={`${num(t.validOrders)} valid · ${num(t.cancelledOrders)} cancelled`}
            onClick={orders('Orders placed', t.orders)} />
          <StatCard size="sm" label="Cancelled orders" value={num(t.cancelledOrders)} tone="rose" icon={<Ban className="h-4 w-4" />}
            info="Always visible, never added to revenue." sub={`${inr(t.cancelledValue)} cancelled value`} onClick={orders('Cancelled orders', t.cancelledOrders, 'cancelled')} />
          <StatCard size="sm" label="Credited by" value={`Tag ${num(t.tagOrders)}`} tone="peach" icon={<MousePointerClick className="h-4 w-4" />}
            info="Credit follows the latest valid Agent Tag on the order, otherwise the agent coupon." sub={`Coupon ${num(t.couponOrders)}`} />
          <StatCard size="sm" label="Unworked" value={num(t.unworked)} tone="butter" icon={<CircleDashed className="h-4 w-4" />}
            info="Work items received but with no agent disposition yet." onClick={work('Unworked', t.unworked, 'unworked')} />
        </div>
      </section>
    </div>
  );
};

export default OverviewTab;
